import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { DOMAIN_CONFIG, sanitizeDomain } from '../config/domainConfig';
import { getSyncZConfig, SyncZConfig } from '../config/synczConfig';
import { synczbase } from '../lib/synczbase';

export interface RuntimeDomains {
  saasRoot: string;
  portalBase: string;
  marketplace: string;
  standalone: string;
  standaloneDomains?: string[];
  host: string;
  origin: string;
}

export interface RuntimeSystemConfig {
  // URLs & Endpoints
  API_URL: string;
  VITE_API_URL: string;
  BACKEND_URL: string;
  POSTGRES_QUERY_URL: string;
  AUTH_LOGIN_URL: string;
  AUTH_ME_URL: string;
  SYNCZBASE_APP_ID: string;
  SYNCZBASE_ENDPOINT: string;
  SYNCZ_PLATFORM_URL: string;
  SYNCZ_PLATFORM_API: string;
  SYNCZBASE_PROXY: string;

  // Security & OAuth
  SYNCZ_PUBLIC_KEY: string;
  SYNCZ_OAUTH_CLIENT_ID: string;
  SYNCZ_OAUTH_REDIRECT_URI: string;

  // Communications
  SYNCZ_SUPPORT_EMAIL: string;
  SYNCZ_PRIVACY_EMAIL: string;
  SYNCZ_SETUP_EMAIL: string;

  // Visitor Tracking
  SYNCZ_VI_TRACK_APP_ID: string;
  SYNCZ_VI_TRACK_DOMAIN: string;
  SYNCZ_VI_TRACK_MODE: string;
  SYNCZ_VI_TRACK_PUBLIC_KEY: string;

  // Domain routing
  domains: RuntimeDomains;

  // Status
  loading: boolean;
  loaded: boolean;
  refreshConfig: () => Promise<void>;
}

const getBrowserOrigin = (): string => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return 'http://localhost:3000';
};

const getBrowserHost = (): string => {
  if (typeof window !== 'undefined' && window.location && window.location.host) {
    return window.location.host;
  }
  return 'localhost:3000';
};

const getBrowserHostname = (): string => {
  if (typeof window !== 'undefined' && window.location && window.location.hostname) {
    return window.location.hostname;
  }
  return 'localhost';
};

const createDefaultConfig = (): RuntimeSystemConfig => {
  const origin = getBrowserOrigin();
  const host = getBrowserHost();
  const hostname = getBrowserHostname();
  const syncz = getSyncZConfig();

  const saasRoot = DOMAIN_CONFIG.SAAS_ROOT || hostname;
  const portalBase = DOMAIN_CONFIG.SAAS_PORTAL_BASE || (saasRoot.includes('.') ? `p.${saasRoot}` : saasRoot);
  const marketplace = DOMAIN_CONFIG.SAAS_MARKETPLACE || (saasRoot.includes('.') ? `marketplace.${saasRoot}` : saasRoot);
  const standalone = DOMAIN_CONFIG.STUDIO_STANDALONE || (saasRoot.includes('.') ? `we.${saasRoot}` : saasRoot);

  const effectiveApi = typeof import.meta.env.VITE_API_URL === 'string' && import.meta.env.VITE_API_URL.trim() !== ''
    ? import.meta.env.VITE_API_URL.trim()
    : origin;

  return {
    API_URL: '',
    VITE_API_URL: '',
    BACKEND_URL: '',
    POSTGRES_QUERY_URL: '/api/postgres/query',
    AUTH_LOGIN_URL: '/api/auth/login',
    AUTH_ME_URL: '/api/auth/me',
    SYNCZBASE_APP_ID: syncz.appId || 'syncz_app',
    SYNCZBASE_ENDPOINT: '',
    SYNCZ_PLATFORM_URL: origin,
    SYNCZ_PLATFORM_API: '/api',
    SYNCZBASE_PROXY: '/api/synczbase',
    SYNCZ_PUBLIC_KEY: syncz.publicKey || 'pub_live_6f7da18c29b6',
    SYNCZ_OAUTH_CLIENT_ID: syncz.clientId || 'syncz_client',
    SYNCZ_OAUTH_REDIRECT_URI: syncz.redirectUri || `${origin}/oauth/callback`,
    SYNCZ_SUPPORT_EMAIL: DOMAIN_CONFIG.SUPPORT_EMAIL || `support@${saasRoot}`,
    SYNCZ_PRIVACY_EMAIL: DOMAIN_CONFIG.PRIVACY_EMAIL || `privacy@${saasRoot}`,
    SYNCZ_SETUP_EMAIL: DOMAIN_CONFIG.SETUP_EMAIL || `setup@${saasRoot}`,
    SYNCZ_VI_TRACK_APP_ID: DOMAIN_CONFIG.VI_TRACK_APP_ID || 'syncz_app',
    SYNCZ_VI_TRACK_DOMAIN: DOMAIN_CONFIG.VI_TRACK_DOMAIN || saasRoot,
    SYNCZ_VI_TRACK_MODE: DOMAIN_CONFIG.VI_TRACK_MODE || 'pixel',
    SYNCZ_VI_TRACK_PUBLIC_KEY: syncz.viTrackPublicKey || 'pub_live_6f7da18c29b6',
    domains: {
      saasRoot,
      portalBase,
      marketplace,
      standalone,
      standaloneDomains: DOMAIN_CONFIG.STANDALONE_DOMAINS,
      host,
      origin
    },
    loading: true,
    loaded: false,
    refreshConfig: async () => {}
  };
};

const ConfigContext = createContext<RuntimeSystemConfig>(createDefaultConfig());

export const useConfig = () => useContext(ConfigContext);

export const ConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<RuntimeSystemConfig>(createDefaultConfig);

  const fetchRuntimeConfig = async () => {
    try {
      const origin = getBrowserOrigin();
      const host = getBrowserHost();

      // 1. Fetch public config & domains internally via relative routes
      const [pubRes, domRes] = await Promise.allSettled([
        fetch('/api/config/public'),
        fetch('/api/config/domains')
      ]);

      let pubData: any = {};
      let domData: any = {};

      if (pubRes.status === 'fulfilled' && pubRes.value.ok) {
        try { pubData = await pubRes.value.json(); } catch (e) {}
      }

      if (domRes.status === 'fulfilled' && domRes.value.ok) {
        try { domData = await domRes.value.json(); } catch (e) {}
      }

      synczbase.setEndpoint('');

      const saasRoot = domData.saasRoot || DOMAIN_CONFIG.SAAS_ROOT || getBrowserHostname();
      const portalBase = domData.portalBase || DOMAIN_CONFIG.SAAS_PORTAL_BASE || (saasRoot.includes('.') ? `p.${saasRoot}` : saasRoot);
      const marketplace = domData.marketplace || DOMAIN_CONFIG.SAAS_MARKETPLACE || (saasRoot.includes('.') ? `marketplace.${saasRoot}` : saasRoot);
      const rawStandalone = domData.standalone || DOMAIN_CONFIG.STUDIO_STANDALONE || (saasRoot.includes('.') ? `we.${saasRoot}` : saasRoot);
      const standaloneList: string[] = Array.isArray(domData.standaloneDomains) && domData.standaloneDomains.length > 0
        ? domData.standaloneDomains
        : (typeof rawStandalone === 'string' && rawStandalone.includes(',')
            ? rawStandalone.split(',').map((s: string) => sanitizeDomain(s)).filter(Boolean)
            : DOMAIN_CONFIG.STANDALONE_DOMAINS);
      const standalone = standaloneList[0] || sanitizeDomain(rawStandalone);

      setConfig({
        API_URL: '',
        VITE_API_URL: '',
        BACKEND_URL: '',
        POSTGRES_QUERY_URL: '/api/postgres/query',
        AUTH_LOGIN_URL: '/api/auth/login',
        AUTH_ME_URL: '/api/auth/me',
        SYNCZBASE_APP_ID: pubData.SYNCZBASE_APP_ID || pubData.VITE_SYNCZ_APP_ID || 'syncz_app',
        SYNCZBASE_ENDPOINT: '',
        SYNCZ_PLATFORM_URL: origin,
        SYNCZ_PLATFORM_API: '/api',
        SYNCZBASE_PROXY: '/api/synczbase',
        SYNCZ_PUBLIC_KEY: pubData.SYNCZ_PUBLIC_KEY || pubData.VITE_SYNCZ_PUBLIC_KEY || 'pub_live_6f7da18c29b6',
        SYNCZ_OAUTH_CLIENT_ID: pubData.SYNCZ_OAUTH_CLIENT_ID || pubData.VITE_SYNCZ_CLIENT_ID || 'syncz_client',
        SYNCZ_OAUTH_REDIRECT_URI: pubData.SYNCZ_OAUTH_REDIRECT_URI || `${origin}/oauth/callback`,
        SYNCZ_SUPPORT_EMAIL: pubData.SYNCZ_SUPPORT_EMAIL || pubData.VITE_SYNCZ_SUPPORT_EMAIL || `support@${saasRoot}`,
        SYNCZ_PRIVACY_EMAIL: pubData.SYNCZ_PRIVACY_EMAIL || pubData.VITE_SYNCZ_PRIVACY_EMAIL || `privacy@${saasRoot}`,
        SYNCZ_SETUP_EMAIL: pubData.SYNCZ_SETUP_EMAIL || pubData.VITE_SYNCZ_SETUP_EMAIL || `setup@${saasRoot}`,
        SYNCZ_VI_TRACK_APP_ID: pubData.SYNCZ_VI_TRACK_APP_ID || pubData.VITE_SYNCZ_TRACK_APP_ID || 'syncz_app',
        SYNCZ_VI_TRACK_DOMAIN: pubData.SYNCZ_VI_TRACK_DOMAIN || pubData.VITE_SYNCZ_TRACK_DOMAIN || saasRoot,
        SYNCZ_VI_TRACK_MODE: pubData.SYNCZ_VI_TRACK_MODE || pubData.VITE_SYNCZ_TRACK_MODE || 'pixel',
        SYNCZ_VI_TRACK_PUBLIC_KEY: pubData.SYNCZ_VI_TRACK_PUBLIC_KEY || pubData.VITE_SYNCZ_VI_TRACK_PUBLIC_KEY || 'pub_live_6f7da18c29b6',
        domains: {
          saasRoot: sanitizeDomain(saasRoot),
          portalBase: sanitizeDomain(portalBase),
          marketplace: sanitizeDomain(marketplace),
          standalone: sanitizeDomain(standalone),
          standaloneDomains: standaloneList,
          host: domData.host || host,
          origin: domData.origin || origin
        },
        loading: false,
        loaded: true,
        refreshConfig: fetchRuntimeConfig
      });
    } catch (err) {
      console.warn('[ConfigProvider] Failed to fetch runtime config, keeping defaults:', err);
      setConfig(prev => ({ ...prev, loading: false, loaded: true, refreshConfig: fetchRuntimeConfig }));
    }
  };

  useEffect(() => {
    fetchRuntimeConfig();
  }, []);

  const value = useMemo(() => ({
    ...config,
    refreshConfig: fetchRuntimeConfig
  }), [config]);

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
};
